import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Gap } from 'ui-components/components/flex';
import { FlexColumn } from 'ui-components/components/flex/column';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { UOM_TYPE_NAME } from '../../../../../../../constants/units-of-measure';
import { SimpleModal } from '../../../../../../../components/simple-modal';

import type { ProductUnitOfMeasureResponse, UomFormState, UomTypeResponse } from '../../uom-types';
import { validateBarcodeManual } from './components/barcode-field/barcode-field';
import { DimensionsSection } from './components/dimensions-section/dimensions-section';
import { FlagsRow } from './components/flags-row/flags-row';
import { IdentifiersSection } from './components/identifiers-section/identifiers-section';
import { UnitRow } from './components/unit-row/unit-row';
import { CONTAINS_ID_UNSET, DIMENSION_MAX } from './uom-modal.constants';
import { useUomValidation } from './uom-modal.validation';
import { messages } from './uom-modal.messages';
import style from './uom-modal.module.less';

const IS_SOLD_STOCKED_ENABLED = false;

const rankOf = (typeName: string, uomTypes: UomTypeResponse[]): number => uomTypes.find((t) => t.name === typeName)?.displayOrder ?? 0;

const containsOptionsFor = (typeName: string, uomTypes: UomTypeResponse[]): string[] => {
  const typeRank = rankOf(typeName, uomTypes);
  return uomTypes.filter((t) => t.displayOrder < typeRank).map((t) => t.name);
};

type RawUnit = {
  productUnitOfMeasureId: number;
  uomTypeName: string;
  parentProductUnitOfMeasureId: number | null;
  conversionFactor: number;
  eaches: number;
};

// Walks up the parent chain of `unit` and returns ordered chain parts starting at `topMultiplier` of that unit.
// e.g. unit=Pack(6 each), topMultiplier=4 → ['4 Pack', '24 Each']
const buildChainFromUnit = (
  unit: RawUnit,
  topMultiplier: number,
  allUnits: RawUnit[],
  compact = false,
  alwaysShowQty = false,
): string[] => {
  const fmt = (qty: number, typeName: string) => {
    if (!compact) return `${qty} ${typeName}`;
    if (qty === 1 && !alwaysShowQty) return typeName;
    return `${qty} x ${typeName}`;
  };
  const byId = new Map(allUnits.map((u) => [u.productUnitOfMeasureId, u]));
  const unitEaches = unit.eaches;
  const parts: string[] = [fmt(topMultiplier, unit.uomTypeName)];
  let current: RawUnit = unit;
  while (current.parentProductUnitOfMeasureId != null) {
    const parent = byId.get(current.parentProductUnitOfMeasureId);
    if (!parent) break;
    const parentEaches = parent.eaches;
    if (parentEaches === 0) break;
    parts.push(fmt(Math.round((topMultiplier * unitEaches) / parentEaches), parent.uomTypeName));
    current = parent;
  }
  // When the parent chain doesn't terminate at the base Each row (it no longer exists as a
  // linkable parent), append the absolute Each breakdown so labels stay e.g. `Pack = 4xEach`.
  if (unitEaches > 1 && current.eaches > 1) {
    parts.push(fmt(topMultiplier * unitEaches, UOM_TYPE_NAME.Each));
  }
  return parts;
};

// Returns the productUnitOfMeasureId of the highest-ranked unit ranked below typeName.
// Falls back to the Each row (or CONTAINS_ID_UNSET if Each not found).
const defaultContainsIdFromUnits = (
  typeName: string,
  existingUnits: { uomTypeName: string; productUnitOfMeasureId: number }[],
  uomTypes: UomTypeResponse[],
): number => {
  const typeRank = rankOf(typeName, uomTypes);
  let bestId = existingUnits.find((u) => u.uomTypeName === UOM_TYPE_NAME.Each)?.productUnitOfMeasureId ?? CONTAINS_ID_UNSET;
  let bestRank = -1;
  for (const u of existingUnits) {
    const r = rankOf(u.uomTypeName, uomTypes);
    if (r < typeRank && r > bestRank) {
      bestId = u.productUnitOfMeasureId;
      bestRank = r;
    }
  }
  return bestId;
};

const buildInitialState = (
  row: ProductUnitOfMeasureResponse | null,
  uomTypes: UomTypeResponse[],
  productSku: string,
  existingUnits: ProductUnitOfMeasureResponse[],
): UomFormState => {
  if (row) {
    const parentUnit = existingUnits.find((u) => u.productUnitOfMeasureId === row.parentProductUnitOfMeasureId);
    const containsId = parentUnit?.productUnitOfMeasureId ?? defaultContainsIdFromUnits(row.uomTypeName, existingUnits, uomTypes);
    const storedBarcode = row.barcode ?? '';
    const autoBase = `${productSku}-${row.uomTypeName}`;
    const barcodeIsAuto = storedBarcode === autoBase || storedBarcode.startsWith(`${autoBase}-`);
    const barcodeSuffix = barcodeIsAuto && storedBarcode !== autoBase ? storedBarcode.slice(autoBase.length + 1) : '';
    return {
      uomTypeId: row.uomTypeId,
      containsId,
      uomSku: row.uomSku ?? '',
      quantity: String(row.conversionFactor),
      // In auto mode barcode is derived at render time — store '' as placeholder
      barcode: barcodeIsAuto ? '' : storedBarcode,
      barcodeIsAuto,
      barcodeSuffix,
      length: row.length != null ? String(row.length) : '',
      width: row.width != null ? String(row.width) : '',
      height: row.height != null ? String(row.height) : '',
      weight: row.weight != null ? String(row.weight) : '',
      isStackable: row.isStackable,
      isSold: row.isSold,
      isStocked: row.isStocked,
    };
  }
  const defaultType = uomTypes[0];
  const defaultTypeName = defaultType?.name ?? UOM_TYPE_NAME.Pack;
  return {
    uomTypeId: defaultType?.uomTypeId ?? 0,
    containsId: defaultContainsIdFromUnits(defaultTypeName, existingUnits, uomTypes),
    uomSku: `${productSku}_${defaultTypeName}`,
    quantity: '1',
    barcode: '',
    barcodeIsAuto: true,
    barcodeSuffix: '',
    length: '',
    width: '',
    height: '',
    weight: '',
    isStackable: true,
    isSold: true,
    isStocked: true,
  };
};

export type UomModalProps = {
  dimensionUnitAbbr?: string;
  existingUnits: ProductUnitOfMeasureResponse[];
  isEditing: boolean;
  productSku: string;
  row: ProductUnitOfMeasureResponse | null;
  uomTypes: UomTypeResponse[];
  weightUnit?: 'ounces' | 'grams';
  onClose: () => void;
  onFormChange?: () => void;
  onSave: (form: UomFormState) => Promise<void>;
};

export const UomModal = ({
  dimensionUnitAbbr,
  existingUnits,
  isEditing,
  productSku,
  row,
  uomTypes,
  weightUnit,
  onClose,
  onFormChange,
  onSave,
}: UomModalProps) => {
  const intl = useIntl();

  const nonEachTypes = useMemo(() => uomTypes.filter((t) => t.name !== UOM_TYPE_NAME.Each), [uomTypes]);

  const [form, setForm] = useState<UomFormState>(() => buildInitialState(row, nonEachTypes, productSku, existingUnits));
  const [isSaving, setIsSaving] = useState(false);

  const currentTypeName = useMemo(() => uomTypes.find((t) => t.uomTypeId === form.uomTypeId)?.name ?? '', [uomTypes, form.uomTypeId]);

  const currentContainsUnit = useMemo(
    () => existingUnits.find((u) => u.productUnitOfMeasureId === form.containsId) ?? null,
    [existingUnits, form.containsId],
  );

  // Derived — never stored in form state when barcodeIsAuto is true
  let effectiveBarcode = form.barcode;
  if (form.barcodeIsAuto) {
    effectiveBarcode = form.barcodeSuffix ? `${productSku}-${currentTypeName}-${form.barcodeSuffix}` : `${productSku}-${currentTypeName}`;
  }

  const { errors, setErrors, barcodeError, setBarcodeError, validation, applyValidation } = useUomValidation({
    form,
    effectiveBarcode,
    existingUnits,
    currentRowId: row?.productUnitOfMeasureId,
  });

  const updateField = useCallback(
    <K extends keyof UomFormState>(field: K, value: UomFormState[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      onFormChange?.();
    },
    [onFormChange],
  );

  const handleTypeChange = useCallback(
    (key: string) => {
      const newTypeId = Number(key);
      const newTypeName = uomTypes.find((t) => t.uomTypeId === newTypeId)?.name ?? '';
      const validTypeNames = containsOptionsFor(newTypeName, uomTypes);
      const currentContainsIsValid = currentContainsUnit != null && validTypeNames.includes(currentContainsUnit.uomTypeName);
      const newContainsId = currentContainsIsValid ? form.containsId : defaultContainsIdFromUnits(newTypeName, existingUnits, uomTypes);
      const isSkuAutoGenerated = form.uomSku === `${productSku}_${currentTypeName}`;
      const newUomSku = isSkuAutoGenerated ? `${productSku}_${newTypeName}` : form.uomSku;

      setForm((prev) => ({ ...prev, uomTypeId: newTypeId, containsId: newContainsId, uomSku: newUomSku }));
      setErrors((prev) => ({ ...prev, uomTypeId: null }));
      onFormChange?.();
    },
    [uomTypes, existingUnits, form.containsId, form.uomSku, currentTypeName, currentContainsUnit, productSku, onFormChange, setErrors],
  );

  const handleContainsChange = useCallback(
    (key: string) => {
      updateField('containsId', Number(key));
      setErrors((prev) => ({ ...prev, containsId: null }));
    },
    [updateField, setErrors],
  );

  const handleBarcodeChange = useCallback(
    (value: string) => {
      updateField('barcode', value);
      setBarcodeError(value ? validateBarcodeManual(value) : null);
    },
    [updateField, setBarcodeError],
  );

  const handleToggleManual = useCallback(() => {
    // Copy the current effective barcode (including any suffix) into barcode so the input pre-fills with it
    setForm((prev) => ({ ...prev, barcodeIsAuto: false, barcode: effectiveBarcode, barcodeSuffix: '' }));
    onFormChange?.();
  }, [effectiveBarcode, onFormChange]);

  const handleRegenerate = useCallback(() => {
    setForm((prev) => ({ ...prev, barcodeSuffix: crypto.randomUUID().slice(0, 8) }));
    setBarcodeError(null);
  }, [setBarcodeError]);

  const handleUseAuto = useCallback(() => {
    setForm((prev) => ({ ...prev, barcodeIsAuto: true, barcode: '', barcodeSuffix: '' }));
    setBarcodeError(null);
    onFormChange?.();
  }, [onFormChange, setBarcodeError]);

  const handleBarcodeBlur = useCallback(() => {
    if (!form.barcodeIsAuto && form.barcode) {
      setBarcodeError(validateBarcodeManual(form.barcode));
    }
  }, [form.barcodeIsAuto, form.barcode, setBarcodeError]);

  const handleDimensionChange = useCallback(
    (field: 'length' | 'width' | 'height', v: number | undefined) => {
      const isEmpty = v == null || v === 0;
      updateField(field, isEmpty ? '' : String(v));
      let dimError: string | null = null;
      if (!isEmpty && (Number.isNaN(v) || (v as number) < 0)) dimError = 'invalid';
      else if (!isEmpty && (v as number) > DIMENSION_MAX) dimError = 'tooLarge';
      setErrors((prev) => ({ ...prev, [field]: dimError }));
    },
    [updateField, setErrors],
  );

  const handleWeightChange = useCallback((value: number) => updateField('weight', value === 0 ? '' : String(value)), [updateField]);

  const conversionSummary = useMemo((): string => {
    if (!currentTypeName) return '—';
    const qty = Number(form.quantity);
    const hasValidQty = form.quantity !== '' && qty >= 1;
    if (!hasValidQty || !currentContainsUnit) return `${currentTypeName} = ?`;
    const chainParts = buildChainFromUnit(currentContainsUnit, qty, existingUnits, true, true);
    return [currentTypeName, ...chainParts].join(' = ');
  }, [currentTypeName, currentContainsUnit, form.quantity, existingUnits]);

  const typeItems = useMemo(() => nonEachTypes.map((t) => ({ key: String(t.uomTypeId), name: t.name })), [nonEachTypes]);

  const containsItems = useMemo(() => {
    const validTypeNames = containsOptionsFor(currentTypeName, uomTypes);
    return existingUnits.map((unit) => {
      const isDisabled = !validTypeNames.includes(unit.uomTypeName);
      const label = buildChainFromUnit(unit, 1, existingUnits, true).join(' = ');
      const name = isDisabled ? `${label} — ${intl.formatMessage(messages.containsNotSmaller, { typeName: currentTypeName })}` : label;
      return { key: String(unit.productUnitOfMeasureId), name, disabled: isDisabled };
    });
  }, [currentTypeName, existingUnits, intl, uomTypes]);

  const handleSave = useCallback(async () => {
    if (!applyValidation()) return;
    setIsSaving(true);
    try {
      // Pass effectiveBarcode so the caller always gets the resolved value
      await onSave({ ...form, barcode: effectiveBarcode });
    } catch {
      // onSave handles error notification; modal stays open
    } finally {
      setIsSaving(false);
    }
  }, [applyValidation, form, effectiveBarcode, onSave]);

  const headerTitle = (
    <>
      {intl.formatMessage(isEditing ? messages.editTitle : messages.addTitle)}
      <span className={style['header-sku']}> {intl.formatMessage(messages.headerSkuSuffix, { productSku })}</span>
    </>
  );

  return (
    <SimpleModal
      actionButtonTheme={THEME_CONSTANTS.primary}
      actionMessage={isSaving ? messages.savingLabel : messages.saveUnit}
      cancelMessage={messages.cancel}
      headerMessage={headerTitle}
      isActionDisabled={isSaving || !validation.isValid}
      onAction={handleSave}
      onCancel={isSaving ? undefined : onClose}
      onClose={isSaving ? undefined : onClose}
      size='medium'
    >
      <FlexColumn className={isSaving ? style['saving-overlay'] : undefined} gap={Gap.Space16}>
        <UnitRow
          containsError={errors.containsId ?? null}
          containsItems={containsItems}
          containsValue={String(form.containsId)}
          conversionSummary={conversionSummary}
          currentContainsUnit={currentContainsUnit}
          currentTypeName={currentTypeName}
          quantityError={errors.quantity ?? null}
          quantityValue={form.quantity}
          typeError={errors.uomTypeId ?? null}
          typeItems={typeItems}
          typeValue={String(form.uomTypeId)}
          onContainsChange={handleContainsChange}
          onTypeChange={handleTypeChange}
          setQuantityError={(error) => setErrors((prev) => ({ ...prev, quantity: error }))}
          updateQuantity={(value) => updateField('quantity', value)}
        />

        <IdentifiersSection
          barcodeError={barcodeError}
          effectiveBarcode={effectiveBarcode}
          form={form}
          isDuplicateBarcode={validation.isDuplicateBarcode}
          isDuplicateUomSku={validation.isDuplicateUomSku}
          onBarcodeBlur={handleBarcodeBlur}
          onBarcodeChange={handleBarcodeChange}
          onRegenerate={handleRegenerate}
          onToggleManual={handleToggleManual}
          onUomSkuChange={(value) => updateField('uomSku', value)}
          onUseAuto={handleUseAuto}
        />

        <DimensionsSection
          dimensionUnitAbbr={dimensionUnitAbbr}
          errors={errors}
          form={form}
          weightUnit={weightUnit}
          onDimensionChange={handleDimensionChange}
          onWeightChange={handleWeightChange}
        />

        <FlagsRow
          isStackable={form.isStackable}
          isSold={form.isSold}
          isStocked={form.isStocked}
          isSoldStockedEnabled={IS_SOLD_STOCKED_ENABLED}
          onChangeStackable={(v) => updateField('isStackable', v)}
          onChangeIsSold={(v) => updateField('isSold', v)}
          onChangeIsStocked={(v) => updateField('isStocked', v)}
        />
      </FlexColumn>
    </SimpleModal>
  );
};
